import {
  Box, Checkbox, Divider, FormControlLabel, Popover,
} from '@mui/material';
import
{
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useState,
  JSX,
} from 'react';
import CustomButton from '../Button';
import { ExportOptions } from '../../../types/Export/ExportOptions.types';
import { buildAndDownloadCSV } from './buildAndDownloadCSV';

interface IProps<T> {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  exportOptions: ExportOptions<T>;
  getData: () => T[] | Promise<T[]>;
  filenamePrefix: string;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
}

export default function ExportOptionsPopover<T>({
  anchorEl,
  onClose,
  exportOptions,
  getData,
  filenamePrefix,
  loading,
  setLoading,
}: IProps<T>): JSX.Element {
  const [options, setOptions] = useState<ExportOptions<T>>(exportOptions);

  const handleSelect = (event:ChangeEvent<HTMLInputElement>): void => {
    const key = event.target.value;
    setOptions((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        selected: event.target.checked,
      },
    }));
  };

  const handleExport = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await getData();
      buildAndDownloadCSV(data, options, filenamePrefix);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box
        display="flex"
        flexDirection="column"
        width="260px"
        maxHeight="320px"
        overflow="auto"
        padding="16px"
      >
        {Object.keys(options).map((key) => (
          <FormControlLabel
            key={key}
            value={key}
            control={(
              <Checkbox
                checked={options[key].selected}
                onChange={handleSelect}
              />
            )}
            label={options[key].label}
            labelPlacement="end"
            sx={{ height: '42px' }}
          />
        ))}
      </Box>
      <Divider />
      <Box
        display="flex"
        justifyContent="center"
        padding="16px"
      >
        <Box component="span">
          <CustomButton
            style={{ width: '100%' }}
            variant="bold"
            size="medium"
            label="Export as CSV"
            onClick={(): void => {
              handleExport();
            }}
            loading={loading}
            disabled={Object.values(options).every((option) => !option.selected)}
          />
        </Box>
      </Box>
    </Popover>
  );
}
